import { useCallback, useEffect, useRef, useState } from "react"
import type React from "react"
import type { RefObject, SetStateAction } from "react"

import {
  addDays,
  addMinutes,
  differenceInMinutes,
  format,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns"
import { toast } from "sonner"

import type { UISlotType } from "@/lib/plannedRouteMapping"
import { layoutOffsetX, layoutPixelScale } from "@/lib/pointerCoords"
import { autoAdjustStops } from "@/lib/stopSchedule"
import { positionToTime, snapToInterval } from "@/lib/timelineScale"
import type { PlannedRoute, PlannedStop } from "@/types/collections"

import {
  MAX_STOP_DURATION_HOURS,
  SNAP_INTERVAL,
  type DragCreateState,
  type DragState,
  type DragType,
  type NewStopFormData,
  type PendingStopData,
} from "./types"

export function useTimelineDrag({
  timelineContentRef,
  selectedDate,
  hourWidth,
  routes,
  filteredRoutes,
  editRoutes,
  openNewStopDialog,
}: {
  timelineContentRef: RefObject<HTMLDivElement>
  selectedDate: Date
  hourWidth: number
  routes: PlannedRoute[]
  filteredRoutes: PlannedRoute[]
  editRoutes: (update: (prevRoutes: PlannedRoute[]) => PlannedRoute[]) => void
  openNewStopDialog: (pending: PendingStopData, form: SetStateAction<NewStopFormData>) => void
}) {
  const [dragCreateState, setDragCreateState] = useState<DragCreateState | null>(null)
  const [isCreatingStop, setIsCreatingStop] = useState(false)

  const [dragState, setDragState] = useState<DragState | null>(null)
  const isDraggingRef = useRef(false)
  // Read through a ref so each staged edit does not rebind the drag's window listeners.
  const routesRef = useRef(routes)
  routesRef.current = routes

  const [dropTargetRowIndex, setDropTargetRowIndex] = useState<number | null>(null)

  const pixelToTime = useCallback(
    (x: number) => positionToTime(x, selectedDate, hourWidth),
    [selectedDate, hourWidth],
  )

  const handleDragStart = useCallback(
    (e: React.MouseEvent, rowIndex: number) => {
      if (e.button !== 0) return

      // Content-relative layout pixels, so it can be added to scrollLeft. See lib/pointerCoords.
      const content = timelineContentRef.current
      if (!content) return

      const x = layoutOffsetX(content, e.clientX) + content.scrollLeft
      const startTime = snapToInterval(pixelToTime(x), SNAP_INTERVAL)

      setDragCreateState({
        rowIndex,
        startX: x,
        currentX: x,
        startTime,
        endTime: addMinutes(startTime, 15),
      })
      setIsCreatingStop(true)
    },
    [pixelToTime, timelineContentRef],
  )

  const handleDragMove = useCallback(
    (e: MouseEvent) => {
      if (!dragCreateState || !isCreatingStop) return

      const content = timelineContentRef.current
      if (!content) return

      const x = layoutOffsetX(content, e.clientX) + content.scrollLeft
      const endTime = snapToInterval(
        pixelToTime(Math.max(x, dragCreateState.startX + hourWidth * 0.25)),
        SNAP_INTERVAL,
      )

      setDragCreateState((prev) =>
        prev
          ? {
              ...prev,
              currentX: x,
              endTime:
                endTime.getTime() > prev.startTime.getTime()
                  ? endTime
                  : addMinutes(prev.startTime, 15),
            }
          : null,
      )
    },
    [dragCreateState, isCreatingStop, pixelToTime, hourWidth, timelineContentRef],
  )

  const handleDragEnd = useCallback(() => {
    if (!dragCreateState || !isCreatingStop) return

    const route = filteredRoutes[dragCreateState.rowIndex]
    if (route) {
      const startTimeStr = format(dragCreateState.startTime, "HH:mm")
      const endTimeStr = format(dragCreateState.endTime, "HH:mm")

      openNewStopDialog(
        {
          routeId: route.id,
          startTime: dragCreateState.startTime,
          endTime: dragCreateState.endTime,
        },
        (prev) => ({
          ...prev,
          startTime: startTimeStr,
          endTime: endTimeStr,
        }),
      )
    }

    setDragCreateState(null)
    setIsCreatingStop(false)
  }, [dragCreateState, isCreatingStop, filteredRoutes, openNewStopDialog])

  useEffect(() => {
    if (isCreatingStop) {
      window.addEventListener("mousemove", handleDragMove)
      window.addEventListener("mouseup", handleDragEnd)
      document.body.style.cursor = "crosshair"
      document.body.style.userSelect = "none"

      return () => {
        window.removeEventListener("mousemove", handleDragMove)
        window.removeEventListener("mouseup", handleDragEnd)
        document.body.style.cursor = ""
        document.body.style.userSelect = ""
      }
    }
  }, [isCreatingStop, handleDragMove, handleDragEnd])

  const handleSlotDragOver = useCallback(
    (e: React.DragEvent, rowIndex: number) => {
      if (isDraggingRef.current || dragState) return

      const types = e.dataTransfer.types
      if (!types.includes("application/json")) return

      e.preventDefault()
      e.stopPropagation()
      e.dataTransfer.dropEffect = "copy"
      setDropTargetRowIndex(rowIndex)
    },
    [dragState],
  )

  const handleSlotDragLeave = useCallback((e: React.DragEvent) => {
    // Ignore moves into a child element.
    const relatedTarget = e.relatedTarget as HTMLElement
    const currentTarget = e.currentTarget as HTMLElement
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return
    }
    setDropTargetRowIndex(null)
  }, [])

  const handleSlotDrop = useCallback(
    (e: React.DragEvent, rowIndex: number) => {
      e.preventDefault()
      e.stopPropagation()
      setDropTargetRowIndex(null)

      if (isDraggingRef.current || dragState) return

      try {
        const jsonData = e.dataTransfer.getData("application/json")
        if (!jsonData) return

        const data = JSON.parse(jsonData)
        if (data.type !== "unscheduled-slot") return

        const route = filteredRoutes[rowIndex]
        if (!route) return

        const [startH, startM] = (data.startTime || "09:00").split(":").map(Number)
        const [endH, endM] = (data.endTime || "10:00").split(":").map(Number)

        let durationMinutes = endH * 60 + endM - (startH * 60 + startM)
        if (durationMinutes <= 0) durationMinutes += 24 * 60

        if (data.serviceTimeMinutes && data.serviceTimeMinutes > 0) {
          durationMinutes = data.serviceTimeMinutes
        }

        const baseDate = startOfDay(selectedDate)
        const startTime = setMinutes(setHours(baseDate, startH), startM)
        const endTime = addMinutes(startTime, Math.max(durationMinutes, 15))

        const slotTypeMap: Record<string, UISlotType> = {
          shipper_collection: "Shipper Collection",
          internal_stop: "Internal Stop",
          haulage_collection: "Haulage Collection",
          haulage_delivery: "Haulage Delivery",
          depot_injection: "Depot Injection",
          direct_injection: "Direct Injection",
          charging_stop: "Charging Stop",
        }

        openNewStopDialog(
          {
            routeId: route.id,
            startTime,
            endTime,
          },
          {
            locationId: data.locationId,
            startTime: format(startTime, "HH:mm"),
            endTime: format(endTime, "HH:mm"),
            slotType: slotTypeMap[data.slotType] || "Shipper Collection",
            slotService: data.slotServices || [],
            slotPnl: data.slotPnl || [],
          },
        )

        toast.success("Slot dropped", {
          description: `Adding ${data.locationName} to ${route.code} at ${format(startTime, "HH:mm")}-${format(endTime, "HH:mm")}`,
        })
      } catch (error) {
        console.error("Failed to handle slot drop:", error)
      }
    },
    [filteredRoutes, dragState, selectedDate, openNewStopDialog],
  )

  const handleStopDragStart = useCallback(
    (e: React.MouseEvent, stop: PlannedStop, route: PlannedRoute, type: DragType) => {
      e.preventDefault()
      e.stopPropagation()
      isDraggingRef.current = true

      setDropTargetRowIndex(null)

      setDragState({
        routeId: route.id,
        stopId: stop.id,
        type,
        startX: e.clientX,
        originalStop: { ...stop },
        originalStops: [...route.stops],
      })
    },
    [],
  )

  const handleStopDragMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState || !isDraggingRef.current) return

      // clientX is painted, hourWidth is layout; convert before combining. See lib/pointerCoords.
      const deltaX =
        (e.clientX - dragState.startX) * layoutPixelScale(timelineContentRef.current)
      const deltaMinutes = (deltaX / hourWidth) * 60

      const route = routesRef.current.find((r) => r.id === dragState.routeId)
      if (!route) return

      const originalStop = dragState.originalStop
      let newArrival: Date
      let newDeparture: Date

      if (dragState.type === "move") {
        newArrival = snapToInterval(
          addMinutes(originalStop.plannedArrival, deltaMinutes),
          SNAP_INTERVAL,
        )
        newDeparture = snapToInterval(
          addMinutes(originalStop.plannedDeparture, deltaMinutes),
          SNAP_INTERVAL,
        )
      } else if (dragState.type === "resize-left") {
        newArrival = snapToInterval(
          addMinutes(originalStop.plannedArrival, deltaMinutes),
          SNAP_INTERVAL,
        )
        newDeparture = originalStop.plannedDeparture
        if (differenceInMinutes(newDeparture, newArrival) < 15) {
          newArrival = addMinutes(newDeparture, -15)
        }
      } else {
        newArrival = originalStop.plannedArrival
        newDeparture = snapToInterval(
          addMinutes(originalStop.plannedDeparture, deltaMinutes),
          SNAP_INTERVAL,
        )
        if (differenceInMinutes(newDeparture, newArrival) < 15) {
          newDeparture = addMinutes(newArrival, 15)
        }
      }

      if (differenceInMinutes(newDeparture, newArrival) > MAX_STOP_DURATION_HOURS * 60) {
        if (dragState.type === "resize-left") {
          newArrival = addMinutes(newDeparture, -(MAX_STOP_DURATION_HOURS * 60))
        } else {
          newDeparture = addMinutes(newArrival, MAX_STOP_DURATION_HOURS * 60)
        }
      }

      // routes stores stops on effective_from: keep only the time and apply it to the route's date.
      const routeBaseDate = startOfDay(route.date)
      const convertToRouteDate = (time: Date): Date => {
        const timeStr = format(time, "HH:mm:ss")
        const timeDate = startOfDay(time)
        const selectedDateStart = startOfDay(selectedDate)
        const dayOffset = Math.round(
          (timeDate.getTime() - selectedDateStart.getTime()) / (1000 * 60 * 60 * 24),
        )
        const targetDate = addDays(routeBaseDate, dayOffset)
        return new Date(`${format(targetDate, "yyyy-MM-dd")}T${timeStr}`)
      }

      const routeArrival = convertToRouteDate(newArrival)
      const routeDeparture = convertToRouteDate(newDeparture)

      const originalStopsInRouteDate = dragState.originalStops.map((stop) => {
        const arrivalConverted = convertToRouteDate(stop.plannedArrival)
        const departureConverted = convertToRouteDate(stop.plannedDeparture)
        return {
          ...stop,
          plannedArrival: arrivalConverted,
          plannedDeparture: departureConverted,
        }
      })

      const adjustedStops = autoAdjustStops(
        originalStopsInRouteDate,
        dragState.stopId,
        routeArrival,
        routeDeparture,
      )

      editRoutes((prevRoutes) =>
        prevRoutes.map((r) =>
          r.id === dragState.routeId
            ? {
                ...r,
                stops: adjustedStops,
                plannedStartTime: adjustedStops[0]?.plannedArrival || r.plannedStartTime,
                plannedEndTime:
                  adjustedStops[adjustedStops.length - 1]?.plannedDeparture || r.plannedEndTime,
                updatedAt: new Date(),
              }
            : r,
        ),
      )
    },
    [dragState, hourWidth, selectedDate, editRoutes, timelineContentRef],
  )

  const handleStopDragEnd = useCallback(() => {
    isDraggingRef.current = false
    setDragState(null)
  }, [])

  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleStopDragMove)
      window.addEventListener("mouseup", handleStopDragEnd)
      document.body.style.cursor = dragState.type === "move" ? "grabbing" : "ew-resize"
      document.body.style.userSelect = "none"

      return () => {
        window.removeEventListener("mousemove", handleStopDragMove)
        window.removeEventListener("mouseup", handleStopDragEnd)
        document.body.style.cursor = ""
        document.body.style.userSelect = ""
      }
    }
  }, [dragState, handleStopDragMove, handleStopDragEnd])

  const resetStopDrag = useCallback(() => {
    setDragState(null)
    isDraggingRef.current = false
  }, [])

  return {
    dragCreateState,
    dragState,
    dropTargetRowIndex,
    handleDragStart,
    handleSlotDragOver,
    handleSlotDragLeave,
    handleSlotDrop,
    handleStopDragStart,
    resetStopDrag,
  }
}
